import React from 'react';
import styled from 'styled-components';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import themeify from '@99designs/apps/spa/images/theme-ify.png';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { isFixedPriceFragment } from '@99designs/apps/spa/pages/create/util/filterProducts';
import { Box, Button, Heading, Link, PageHeading, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { linkTo } from '@99designs/routing';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';

const CardContainer = styled.div`
    > * {
        background-color: white;
        z-index: 20;
    }
`;

const ImageContainer = styled.div`
    position: absolute;
    width: 65%;
    z-index: -10;
    overflow: hidden;

    ${mediaQuery.minimum('normal')} {
        top: -5px;
        right: -90px;
    }

    ${mediaQuery.between('small', 'medium')} {
        display: none;
    }
`;

export default function ThemeifyYourLogoProductSelection() {
    const { error, data, loading } = useProductsQuery({
        variables: { input: { names: ['theme-ify-your-logo'] } },
    });

    if (error) {
        throw error;
    }

    if (loading || !data) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    const product = data.products[0];

    if (!isFixedPriceFragment(product)) {
        return null;
    }

    const packageForProductSummary = {
        title: product.title,
        description: product.description,
        amountInCents: product.pricing.amount.amountInCents,
        currency: product.pricing.amount.currency,
    };

    return (
        <ContentSetting>
            <Box marginTop="1.5bu" marginBottom="1.5bu">
                <PageHeading>{__('Get in the holiday spirit')}</PageHeading>
            </Box>

            <Box marginTop={['none', '2bu']} position="relative">
                <Stack space="1.5bu">
                    <CardContainer key={product.key}>
                        <ProductCard
                            rightColumn={
                                <Stack space="1bu">
                                    <Box display="flex" justifyContent="center">
                                        <ViewProductDetails
                                            features={product.details}
                                            additionalDetails={product.additionalDetails}
                                            productSummary={packageToProductSummary({
                                                ...packageForProductSummary,
                                            })}
                                            image={product.image}
                                            headerVideo={product.headerVideo}
                                        />
                                    </Box>
                                    <ButtonWrapper>
                                        <Button
                                            size="large"
                                            appearance="primary"
                                            width="fill"
                                            href={linkTo.briefCreate(product.key)}
                                            type="button"
                                        >
                                            {__('Start brief')}
                                        </Button>
                                    </ButtonWrapper>
                                </Stack>
                            }
                            productSummary={packageToProductSummary(packageForProductSummary)}
                            features={product.details}
                            hasLeftBorder={false}
                        />
                    </CardContainer>
                </Stack>
            </Box>

            <Box marginTop="8bu" maxWidth="620px">
                <Stack space="0.75bu">
                    <Heading appearanceLevel={5} accessibilityLevel={5}>
                        {__('Looking for something more custom?')}
                    </Heading>
                    <div>
                        <FormattedReactMessage
                            message={__(
                                `Require extra revisions or maybe multiple versions? <Link>Get tailored quotes from up to 5 designers</Link>, based on your specific needs.`
                            )}
                            replacements={{
                                Link: (
                                    <Link href="https://99designs.com/briefs/create?category=logo-design" />
                                ),
                            }}
                        />
                    </div>
                </Stack>
            </Box>
            <ImageContainer>
                <img alt="theme-ify your logo" src={themeify} />
            </ImageContainer>
        </ContentSetting>
    );
}
